import { KEYS } from '../../constants/keys';
import { focusOnSearchElements } from '../../dynamicBundle/index.js';
import { setSessionStorage, getSessionStorage } from '../../utils';
import { isKeyboardActivated } from '../../utils/keyboardActivated.js';
import {
  MODAL_WRAPPER_SELECTOR,
  PRE_FIX_CLASS_VOLUME_BUNDLE_MODAL,
  VOLUME_BUNDLE_NAME_TEMPLATE,
  KEY_VOLUME_BUNDLE_OPENING,
  DROPDOWN_SELECT_OPTION_SELECTED,
  VOLUME_BUTTON_ADD_BUNDLE_TO_CART_SELECTOR,
} from '../constants';
import { addVolumeBundleToCart, getVolumeBundleDataByWidgetId } from '../services/bundle-data';
import { renderVolumeBundleWithData } from '../ui/bundle-render';
import { handleVolumeModalEvents } from './bundle-events';
import { closeAllDropdown, handleDropdownToggle } from './dropdown-events';
import { initBasicEventsForVolumeBundle } from './radio-events';

/**
 * @type {import('../index.d').openVolumeBundleModal}
 */
export const openVolumeBundleModal = async (context, widgetId) => {
  const bundleModal = document.getElementById(
    `${PRE_FIX_CLASS_VOLUME_BUNDLE_MODAL}${context.blockType}`
  );
  const wrapperModal = bundleModal?.querySelector(MODAL_WRAPPER_SELECTOR);
  const volumeBundleData = getVolumeBundleDataByWidgetId(context, widgetId);
  setSessionStorage(KEY_VOLUME_BUNDLE_OPENING, widgetId);

  renderVolumeBundleWithData(
    context,
    widgetId,
    volumeBundleData,
    wrapperModal,
    VOLUME_BUNDLE_NAME_TEMPLATE.bundlePopup,
    function showModalVolumeBundle() {
      if (wrapperModal) {
        bundleModal.style.display = 'flex';
        document.body.classList.add('boost-sd__modal-open-volume');
        const firstFocusable =
          bundleModal?.querySelector('[tabindex="0"]') ||
          bundleModal?.querySelector(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
          );
        if (firstFocusable) {
          firstFocusable.focus();
        }
      }
    }
  );
};

/**
 * @type {import('../index.d').closeVolumeBundleModal}
 */
export const closeVolumeBundleModal = (context) => {
  const widgetId = getSessionStorage(KEY_VOLUME_BUNDLE_OPENING);
  const bundleModal = document.getElementById(
    `${PRE_FIX_CLASS_VOLUME_BUNDLE_MODAL}${context.blockType}`
  );

  if (bundleModal) {
    bundleModal.style.display = 'none';
    document.body.classList.remove('boost-sd__modal-open-volume');
    if (isKeyboardActivated()) {
      const bundleBlock = document?.querySelector(
        `.boost-sd__volume-bundle-product-item[data-widget-id = "${widgetId}"]`
      );
      bundleBlock?.focus();
    }
  }
};

/**
 * @type {import('../index.d').closeVolumeModalAndFocus}
 */
export const closeVolumeModalAndFocus = (context, idModalByBlock) => {
  closeVolumeBundleModal(context);

  if (idModalByBlock === 'boost-sd__dynamic-bundle-modal-instantSearch') {
    focusOnSearchElements();
  }
};

/**
 * @type {import('../index.d').createVolumeBundleModalContainer}
 */
export const createVolumeBundleModalContainer = (context, position) => {
  const idModalByBlock = `${PRE_FIX_CLASS_VOLUME_BUNDLE_MODAL}${context.blockType}`;

  let bundleModal = document.getElementById(idModalByBlock);

  if (!bundleModal) {
    const volumeModal = document.createElement('div');
    volumeModal.id = idModalByBlock;
    volumeModal.className = 'boost-sd__modal';
    volumeModal.setAttribute('ariaModal', 'true');
    volumeModal.setAttribute('role', 'dialog');
    volumeModal.setAttribute('ariaLabel', 'modal');

    const backdrop = document.createElement('div');
    backdrop.className = 'boost-sd__modal-backdrop';

    const modalContainer = document.createElement('div');
    modalContainer.className = 'boost-sd__modal-container';
    modalContainer.style.maxWidth = '840px';

    const modalWrapper = document.createElement('div');
    modalWrapper.className = 'boost-sd__modal-wrapper';
    modalWrapper.setAttribute('tabIndex', '-1');

    modalContainer.appendChild(modalWrapper);

    volumeModal.appendChild(backdrop);
    volumeModal.appendChild(modalContainer);

    volumeModal.style.display = 'none';

    document.body.appendChild(volumeModal);

    bundleModal = volumeModal;
  }

  bundleModal?.addEventListener('click', function (event) {
    handleVolumeModalEvents(event, context, bundleModal, position);
  });

  bundleModal?.addEventListener('keydown', (event) => {
    const target = event.target;
    const combobox = target?.closest('.boost-sd__dropdown-volume');
    if (combobox) {
      const expanded = combobox?.getAttribute('aria-expanded') === 'true';
      const comboboxList = combobox?.querySelector('[role="listbox"]');

      const comboboxItems = comboboxList?.querySelectorAll('[role="option"]');
      let selectedIndex = Array?.from(comboboxItems)?.findIndex((item) =>
        item.classList?.contains(DROPDOWN_SELECT_OPTION_SELECTED)
      );

      const isArrowUp = event.key === 'ArrowUp';
      const isArrowDown = event.key === 'ArrowDown';
      const isEscPressed = event.key === 'Escape' || event.keyCode === 27;

      if (expanded && (isArrowUp || isArrowDown)) {
        event.preventDefault();
        if (isArrowDown) {
          selectedIndex = (selectedIndex + 1) % comboboxItems.length;
        } else if (isArrowUp) {
          selectedIndex = (selectedIndex - 1 + comboboxItems.length) % comboboxItems.length;
        }
        comboboxItems.forEach((item) => item.classList.remove(DROPDOWN_SELECT_OPTION_SELECTED));
        comboboxItems[selectedIndex].classList.add(DROPDOWN_SELECT_OPTION_SELECTED);
        combobox.setAttribute('aria-activedescendant', comboboxItems[selectedIndex].id);
        comboboxItems[selectedIndex].scrollIntoView({ block: 'nearest' });
        comboboxItems[selectedIndex].focus();
      }

      if (expanded && KEYS.includes(event.key)) {
        const target = event.target;
        initBasicEventsForVolumeBundle(context, target, event);
      }

      if (expanded && isEscPressed) {
        combobox.setAttribute('aria-expanded', 'false');
        closeAllDropdown();
      }
    }

    if (KEYS.includes(event.key)) {
      if (target?.closest(VOLUME_BUTTON_ADD_BUNDLE_TO_CART_SELECTOR)) {
        addVolumeBundleToCart(context, target, context.document);
      }

      handleDropdownToggle(target, combobox);

      if (target?.closest('.boost-sd__volume-bundle-modal-close')) {
        closeVolumeModalAndFocus(context, idModalByBlock);
      }
    }

    if (event.key === 'Escape') {
      closeVolumeModalAndFocus(context, idModalByBlock);
    }
  });
};
